import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Table, Input, Button, Tag, message, Select, Pagination } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Key } from 'react';

import { Slot, PaginationInfo } from '../../../Modules/Slot/Model/Slot';
import { useAppSelector } from '../../../Modules/Presentation/hooks';
import { selectAccounts } from '../../../Modules/Account/Presentation/selectors';
import { getAccountIdById } from '../../../Utils/getAccountNameById';
import { selectUser } from '../../../Modules/User/Presentation/selectors';
import { isSlotOperationRestricted } from '../../../Utils/timeRestriction';

interface Props {
  data: Slot[];
  loading: boolean;
  selected: Key[];
  onSelectChange: (ids: Key[]) => void;
  onInlineUpdate: (id: number, dto: Partial<Slot>) => Promise<void>;
  pagination?: PaginationInfo;
  onPageChange?: (page: number, pageSize?: number) => void;
}

/* 상태별 태그 색 */
const colorMap: Record<Slot['status'], string> = {
  정상: 'green',
  연장: 'green',
  환불: 'red',
  종료: 'orange',
};
const VIRTUAL_ROW_THRESHOLD = 120;
const TABLE_BODY_HEIGHT = 560;
const TABLE_SCROLL_X = 2200;

const SlotTable: React.FC<Props> = ({
  data,
  loading,
  selected,
  onSelectChange,
  onInlineUpdate,
  pagination,
  onPageChange,
}) => {
  const accounts = useAppSelector(selectAccounts);
  const currentUser = useAppSelector(selectUser);
  const statusColumnWidth = (pagination?.page_size ?? data.length) >= 200 ? 82 : 70;

  // 시간 제한 확인
  const isRestricted = currentUser ? isSlotOperationRestricted(currentUser) : false;

  const sellers = useMemo(() => 
    accounts
      .filter(a => a.grade === 'SELLER')
      .sort((a, b) => a.username.localeCompare(b.username)),
    [accounts]
  );

  /* URL 검사 */
  const isValidUrl = useCallback((url?: string) => {
    if (!url) return false;
    try { new URL(url); return true; } catch { return false; }
  }, []);

  /* ───── 컬럼 정의 ───── */
  const columns: ColumnsType<Slot> = useMemo(() => [
    { title: '슬롯 번호', dataIndex: 'slotId', width: 85, align: 'center' },

    {
      title: '상태',
      dataIndex: 'status',
      width: statusColumnWidth,
      render: (v: Slot['status']) => {
        const displayText = v === '연장' ? '정상' : v;
        return <Tag color={colorMap[v]}>{displayText}</Tag>;
      },
    },
    {
      title: '아이디',
      dataIndex: 'user',
      width: 120,
      align: 'center',
      render: (id: number) => getAccountIdById(accounts, id, false, currentUser) ?? '-',
    },
    { title: '시작일', dataIndex: 'start_date', width: 120 },
    { title: '종료일', dataIndex: 'end_date', width: 120 },

    /* ───── 읽기 전용 필드들 ───── */
    {
      title: '메인키워드',
      dataIndex: 'main_keyword',
      render: (v) => v,
    },
    {
      title: '단일 MID',
      dataIndex: 'mid_single',
      width: 120,
      render: (v) => v,
    },
    {
      title: '가격비교 MID',
      dataIndex: 'mid_compare',
      width: 120,
      render: (v) => v,
    },
    {
      title: '상품 URL',
      dataIndex: 'product_url',
      render: (v) => v ? <a href={v} target="_blank" rel="noreferrer">바로가기</a> : '-',
    },
    {
      title: '가격비교 URL',
      dataIndex: 'compare_url',
      render: (v) => v ? <a href={v} target="_blank" rel="noreferrer">바로가기</a> : '-',
    },
    {
      title: '메모',
      dataIndex: 'memo',
      render: (v) => v,
    },
  ], [accounts, currentUser, statusColumnWidth]);

  // 테이블 변경 핸들러
  const handleTableChange = useCallback(
    (pagination: any, filters: any, sorter: any) => {
      // 페이지 변경만 처리
      if (pagination.current !== undefined) {
        onPageChange?.(pagination.current, pagination.pageSize || 20);
      }
    },
    [onPageChange],
  );

  // 페이지네이션 설정
  const paginationProps = pagination ? {
    current: pagination.current_page || 1,
    pageSize: pagination.page_size || 20,
    total: pagination.total_count || 0,
  } : undefined;

  const rowSelection = useMemo(
    () => ({
      type: 'checkbox' as const,
      columnWidth: 44,
      selectedRowKeys: selected,
      onChange: onSelectChange,
    }),
    [selected, onSelectChange]
  );
  const useVirtualTable = data.length >= VIRTUAL_ROW_THRESHOLD;
  const tableScroll = useMemo(
    () =>
      useVirtualTable
        ? { x: TABLE_SCROLL_X, y: TABLE_BODY_HEIGHT }
        : { x: 'max-content' as const },
    [useVirtualTable]
  );

  return (
    <>
      <div className="slot-table" style={{ width: '100%', overflowX: 'auto' }}>
      <Table
        size="small"
        rowKey="slotId"
        dataSource={data}
        columns={columns}
        loading={loading}
        bordered
        rowSelection={rowSelection}
        virtual={useVirtualTable}
        style={{ minWidth: 950 }}
        scroll={tableScroll}
        pagination={false}
        onChange={handleTableChange}
      />
      </div>
      {paginationProps && (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '12px 0' }}>
          <Pagination
            current={paginationProps.current}
            pageSize={paginationProps.pageSize}
            total={paginationProps.total}
            showSizeChanger
            pageSizeOptions={['20','50','100','200','300','400','500','1000']}
            showQuickJumper={false}
            showLessItems
            responsive
            showTotal={(total: number, range: [number, number]) => `${range[0]}-${range[1]} / ${total.toLocaleString()}개 슬롯`}
            onChange={(page, pageSize) => onPageChange?.(page, pageSize)}
            onShowSizeChange={(_, size) => onPageChange?.(1, size)}
          />
        </div>
      )}
    </>
  );
};

export default React.memo(SlotTable); 